import type {
  DatasetField,
  DatasetFieldSpec,
  DatasetFieldType,
} from "@/api/datasets";

/** The field types the server takes, in its order. */
export const DATASET_FIELD_TYPES: readonly DatasetFieldType[] = [
  "text",
  "number",
  "decimal",
  "boolean",
  "date",
  "datetime",
  "select",
];

/** The server's longest field name. */
export const FIELD_NAME_MAX_CHARS = 120;

/** The spec that keeps `field` as it is. */
function specOf(field: DatasetField): DatasetFieldSpec {
  const { id, name, type, required, options, scale } = field;
  return {
    id,
    name,
    type,
    required,
    default: field.default,
    ...(options
      ? { options: options.map(({ id, label }) => ({ id, label })) }
      : {}),
    ...(scale === undefined ? {} : { scale }),
  };
}

/** The full field list with `fieldId` renamed. */
export function renamedField(
  fields: readonly DatasetField[],
  fieldId: string,
  name: string,
): DatasetFieldSpec[] {
  return fields.map((field) =>
    field.id === fieldId ? { ...specOf(field), name } : specOf(field),
  );
}

/** The full field list without `fieldId`: deletes the field and its values. */
export function withoutField(
  fields: readonly DatasetField[],
  fieldId: string,
): DatasetFieldSpec[] {
  return fields.filter((field) => field.id !== fieldId).map(specOf);
}

/** The full field list without one select option: rows holding it lose it. */
export function withoutOption(
  fields: readonly DatasetField[],
  fieldId: string,
  optionId: string,
): DatasetFieldSpec[] {
  return fields.map((field) => {
    const spec = specOf(field);
    if (field.id !== fieldId || !field.options) return spec;
    return {
      ...spec,
      options: field.options
        .filter((option) => option.id !== optionId)
        .map(({ id, label }) => ({ id, label })),
    };
  });
}

/** The full field list with `spec` added at the end. */
export function withField(
  fields: readonly DatasetField[],
  spec: DatasetFieldSpec,
): DatasetFieldSpec[] {
  return [...fields.map(specOf), spec];
}
